/**
 * W13 DM2 — «Why this copy»: turning a screen's `aiMeta.dataMapping` into rows
 * an operator can read.
 *
 * The grammar is NOT re-implemented here: `parseInsightCitationPath`
 * (`strategy-blocks/utils/insightCitationPath.ts`, one of the three
 * byte-identical mirrors of ruling W13-R28) decides what a citation is. Ruling
 * W13-R17 holds on this surface too — the PATH is normative, so a thesis path
 * renders a thesis row and a `…​.sources[<itemId>]` path renders a quote sub-row
 * even when the entry's derived `evidenceKind` says otherwise.
 *
 * Nothing here touches Firebase: the analysis is read by ONE client reader
 * (`firebase-core/InsightThesisService.listEvidence`, IS2/DM2) and handed in.
 */

import {
  parseInsightCitationPath,
  type ParsedInsightCitationPath,
} from '@modules/strategy-blocks/utils/insightCitationPath';

/* ── The resolved analysis, as the panel needs it ──────────────────────────── */

/** One actor's own words behind a citable `originalItemId`. */
export interface InsightEvidenceQuote {
  actorName?: string;
  phrase?: string;
}

export interface InsightEvidenceThesis {
  category: string;
  canonicalDescription: string;
  /** `originalItemId` → the actor's words. */
  quotes: Record<string, InsightEvidenceQuote>;
}

/**
 * analysisId → insightId → thesis. An analysis MISSING from this map was never
 * loaded (or could not be read) — the panel then shows the raw citation path
 * with a note, never a blank line.
 */
export type InsightEvidenceIndex = Record<string, Record<string, InsightEvidenceThesis>>;

/** What a consumer hands the viewer: the index plus how the read went. */
export interface InsightEvidenceState {
  byAnalysis: InsightEvidenceIndex;
  loading?: boolean;
  /** Non-null when the pinned analysis could not be read. */
  error?: string | null;
}

/**
 * One row as the client reader returns it
 * (`firebase-core/InsightThesisService.listEvidence`). Declared structurally so
 * this module stays free of firebase-core — the panel is the only place the two
 * meet.
 */
export interface InsightEvidenceSourceRow {
  id: string;
  category: string;
  canonicalDescription: string;
  quotes: Array<{ originalItemId: string; actorName?: string; phrase?: string }>;
}

/** The rows of ONE analysis, keyed the way a citation addresses them. */
export function buildInsightEvidenceIndex(
  rows: InsightEvidenceSourceRow[] | undefined,
): Record<string, InsightEvidenceThesis> {
  const byInsightId: Record<string, InsightEvidenceThesis> = {};
  for (const row of rows ?? []) {
    if (!row?.id) continue;
    const quotes: Record<string, InsightEvidenceQuote> = {};
    for (const quote of row.quotes ?? []) {
      if (!quote?.originalItemId) continue;
      quotes[quote.originalItemId] = { actorName: quote.actorName, phrase: quote.phrase };
    }
    byInsightId[row.id] = {
      category: row.category,
      canonicalDescription: row.canonicalDescription,
      quotes,
    };
  }
  return byInsightId;
}

/* ── Copy (asserted by the tests; one place to change it) ──────────────────── */

export const EVIDENCE_SECTION_TITLE = 'Why this copy';
export const LONE_QUOTE_HEADING = 'Quote without its thesis';
/**
 * The panel's voice of the DM1 warning `aiMeta.dataMapping.sourceWithoutThesis`
 * — the same sentence the validator prints, minus the screen id the operator is
 * already looking at.
 */
export const LONE_QUOTE_NOTE = 'A quote is supporting evidence, never the grounding citation — '
  + 'the thesis it belongs to is not cited on this screen.';
/** While the read is in flight. NEVER the failure wording — waiting is not failing. */
export const ANALYSIS_LOADING_NOTE = 'Resolving the pinned analysis…';
export const ANALYSIS_UNAVAILABLE_NOTE = 'the pinned analysis could not be read — showing the raw citation';
export const THESIS_NOT_FOUND_NOTE = 'thesis not found in the pinned analysis';
export const SOURCE_NOT_FOUND_NOTE = 'source not found in the pinned analysis';
export const UNKNOWN_ACTOR = 'unknown actor';

/* ── Grouping ─────────────────────────────────────────────────────────────── */

/** One `dataMapping` entry that parsed as an insight citation. */
export interface EvidenceCitation {
  item: Record<string, unknown>;
  parsed: ParsedInsightCitationPath;
  sourcePath: string;
}

/**
 * One RENDERED row: the citation that opens it plus every FURTHER entry of the
 * same list citing the same thing.
 *
 * Fix round 1 (I1 / M1): a screen legitimately grounds two elements — a headline
 * and a subtitle — in one thesis, or quotes one actor twice, with a different
 * `justification` each time. One row is right; dropping the second reason is
 * not. Extras are kept and rendered as «also: …» lines under the row.
 */
export interface EvidenceCitationGroup {
  citation: EvidenceCitation;
  /** Further entries citing the SAME path — never dropped, never a second row. */
  extras: EvidenceCitation[];
}

export interface EvidenceThesisGroup extends EvidenceCitationGroup {
  /** Quote citations of THIS thesis, deduplicated by item id, in declared order. */
  quotes: EvidenceCitationGroup[];
}

export interface GroupedEvidence {
  /** One group per cited thesis, in first-appearance order. */
  groups: EvidenceThesisGroup[];
  /** Quote citations whose thesis this list does not cite — never dropped. */
  loneQuotes: EvidenceCitationGroup[];
  /** Everything that is not an insight citation, untouched. */
  rest: unknown[];
}

/**
 * Splits one citation list into thesis groups, lone quotes and the rest.
 *
 * «Next to its thesis» is judged over the SAME list, exactly as the DM1
 * validator judges it (`findEvidenceCitationDefects`): a quote whose thesis is
 * not in this list is a lone quote here and a warning there.
 *
 * NOTHING is discarded: an entry that is not an insight citation goes to `rest`,
 * a repeated citation becomes an `extra` of the row it repeats.
 */
export function groupInsightCitations(items: unknown): GroupedEvidence {
  const entries = Array.isArray(items) ? items : [];
  const groups: EvidenceThesisGroup[] = [];
  const byThesisPath = new Map<string, EvidenceThesisGroup>();
  const quotes: EvidenceCitation[] = [];
  const rest: unknown[] = [];

  for (const entry of entries) {
    if (!entry || typeof entry !== 'object') {
      rest.push(entry);
      continue;
    }
    const item = entry as Record<string, unknown>;
    const parsed = parseInsightCitationPath(item.sourcePath);
    if (!parsed) {
      rest.push(entry);
      continue;
    }
    const citation: EvidenceCitation = { item, parsed, sourcePath: String(item.sourcePath) };
    if (parsed.sourceItemId) {
      quotes.push(citation);
      continue;
    }
    const existing = byThesisPath.get(parsed.thesisPath);
    if (existing) {
      existing.extras.push(citation);
      continue;
    }
    const group: EvidenceThesisGroup = { citation, extras: [], quotes: [] };
    byThesisPath.set(parsed.thesisPath, group);
    groups.push(group);
  }

  const loneQuotes: EvidenceCitationGroup[] = [];
  /** `<thesisPath>|<itemId>` → the quote row that key already opened. */
  const quoteRows = new Map<string, EvidenceCitationGroup>();

  for (const quote of quotes) {
    const key = `${quote.parsed.thesisPath}|${quote.parsed.sourceItemId}`;
    const opened = quoteRows.get(key);
    if (opened) {
      opened.extras.push(quote);
      continue;
    }
    const row: EvidenceCitationGroup = { citation: quote, extras: [] };
    quoteRows.set(key, row);
    const group = byThesisPath.get(quote.parsed.thesisPath);
    if (group) group.quotes.push(row);
    else loneQuotes.push(row);
  }

  return { groups, loneQuotes, rest };
}

/**
 * The «also: …» lines of a row — one per further entry citing the same path.
 *
 * Each line carries that entry's `justification`, prefixed by its `value` when
 * it differs from the row's own (that is what tells the two elements apart). An
 * entry that carries neither still gets a line: the operator must be able to see
 * that a second element cites this evidence, even when the model said nothing
 * about why.
 */
export function extraLines(group: EvidenceCitationGroup): string[] {
  const ownValue = nonEmpty(group.citation.item.value);
  return group.extras.map((extra) => {
    const value = nonEmpty(extra.item.value);
    const justification = nonEmpty(extra.item.justification);
    const parts: string[] = [];
    if (value && value !== ownValue) parts.push(value);
    if (justification) parts.push(justification);
    return `also: ${parts.length > 0 ? parts.join(' — ') : 'cited again, with no reason given'}`;
  });
}

/* ── Resolution against the pinned analysis ────────────────────────────────── */

export interface ResolvedThesis {
  /** The canonical thesis text, or null when it could not be resolved. */
  text: string | null;
  /** The category the analysis records; falls back to the one in the path. */
  category: string;
  /** Why the text is missing — rendered inline, so the row is never blank. */
  note: string | null;
}

export interface ResolvedQuote {
  /** The quoted text: the citation's own `value` first, then the package. */
  text: string | null;
  actorName: string;
  note: string | null;
}

const nonEmpty = (value: unknown): string | null =>
  (typeof value === 'string' && value.trim() ? value.trim() : null);

/**
 * The note for an analysis that is not in the index (fix round 1, I2): while the
 * read is in flight the row says «resolving», never «could not be read» — the
 * hook sets `loading` between mount and resolution, and claiming a failure there
 * is simply false.
 */
const missingAnalysisNote = (evidence: InsightEvidenceState): string =>
  (evidence.loading ? ANALYSIS_LOADING_NOTE : ANALYSIS_UNAVAILABLE_NOTE);

export function resolveThesis(
  evidence: InsightEvidenceState,
  parsed: ParsedInsightCitationPath,
): ResolvedThesis {
  const analysis = evidence.byAnalysis[parsed.analysisId];
  if (!analysis) {
    return { text: null, category: parsed.category, note: missingAnalysisNote(evidence) };
  }
  const thesis = analysis[parsed.insightId];
  if (!thesis) {
    return { text: null, category: parsed.category, note: THESIS_NOT_FOUND_NOTE };
  }
  return {
    text: thesis.canonicalDescription,
    category: thesis.category || parsed.category,
    note: null,
  };
}

export function resolveQuote(
  evidence: InsightEvidenceState,
  citation: EvidenceCitation,
): ResolvedQuote {
  const { parsed, item } = citation;
  const itemId = parsed.sourceItemId ?? '';
  const analysis = evidence.byAnalysis[parsed.analysisId];
  const variant = analysis?.[parsed.insightId]?.quotes?.[itemId];

  // The citation's own `value` IS the quoted text (DM1's rule for a quote
  // entry); the package phrase only fills in when the entry carries none.
  const text = nonEmpty(item.value) ?? nonEmpty(variant?.phrase);
  // `actorName` on the entry is DERIVED display metadata (W13-R17) — a hint,
  // used only because the package is the slower source, never as grounding.
  const actorName = nonEmpty(item.actorName) ?? nonEmpty(variant?.actorName) ?? UNKNOWN_ACTOR;

  const note = !analysis
    ? missingAnalysisNote(evidence)
    : !analysis[parsed.insightId] || !variant
      ? SOURCE_NOT_FOUND_NOTE
      : null;

  return { text, actorName, note };
}
